// SecurityStore.js - Updated store with better risk scores processing
import { create } from 'zustand';
import isEqual from 'lodash/isEqual';

// Dummy data for repositories
const DUMMY_REPOSITORIES = [
  {
    id: 'facebook/react',
    name: 'react',
    owner: 'facebook',
    verifiedFindings: 8,
    unverifiedFindings: 12,
    totalFindings: 20,
    lastScanDate: new Date('2024-01-15'),
    riskLevel: 'Medium'
  },
  {
    id: 'vercel/next.js',
    name: 'next.js',
    owner: 'vercel',
    verifiedFindings: 15,
    unverifiedFindings: 5,
    totalFindings: 20,
    lastScanDate: new Date('2024-01-14'),
    riskLevel: 'High'
  },
  {
    id: 'microsoft/vscode',
    name: 'vscode',
    owner: 'microsoft',
    verifiedFindings: 3,
    unverifiedFindings: 23,
    totalFindings: 26,
    lastScanDate: new Date('2024-01-13'),
    riskLevel: 'Critical'
  },
  {
    id: 'google/chrome',
    name: 'chrome',
    owner: 'google',
    verifiedFindings: 9,
    unverifiedFindings: 7,
    totalFindings: 16,
    lastScanDate: new Date('2024-01-12'),
    riskLevel: 'Medium'
  },
  {
    id: 'apple/swift',
    name: 'swift',
    owner: 'apple',
    verifiedFindings: 4,
    unverifiedFindings: 18,
    totalFindings: 22,
    lastScanDate: new Date('2024-01-11'),
    riskLevel: 'High'
  },
  {
    id: 'nodejs/node',
    name: 'node',
    owner: 'nodejs',
    verifiedFindings: 11,
    unverifiedFindings: 3,
    totalFindings: 14,
    lastScanDate: new Date('2024-01-10'),
    riskLevel: 'Low'
  },
  {
    id: 'tensorflow/tensorflow',
    name: 'tensorflow',
    owner: 'tensorflow',
    verifiedFindings: 6,
    unverifiedFindings: 14,
    totalFindings: 20,
    lastScanDate: new Date('2024-01-09'),
    riskLevel: 'Medium'
  },
  {
    id: 'kubernetes/kubernetes',
    name: 'kubernetes',
    owner: 'kubernetes',
    verifiedFindings: 7,
    unverifiedFindings: 9,
    totalFindings: 16,
    lastScanDate: new Date('2024-01-08'),
    riskLevel: 'Medium'
  },
  {
    id: 'docker/docker',
    name: 'docker',
    owner: 'docker',
    verifiedFindings: 2,
    unverifiedFindings: 21,
    totalFindings: 23,
    lastScanDate: new Date('2024-01-07'),
    riskLevel: 'Critical'
  },
  {
    id: 'ansible/ansible',
    name: 'ansible',
    owner: 'ansible',
    verifiedFindings: 10,
    unverifiedFindings: 6,
    totalFindings: 16,
    lastScanDate: new Date('2024-01-06'),
    riskLevel: 'Low'
  }
];

// Generate dummy daily risk scores for the last 30 days
const generateDailyRiskScores = () => {
  const scores = [];
  const today = new Date();
  
  for (let i = 29; i >= 0; i--) {
    const date = new Date(today);
    date.setDate(date.getDate() - i);
    
    const verified = Math.floor(Math.random() * 20) + 5;
    const unverified = Math.floor(Math.random() * 30) + 5;
    const total = verified + unverified;
    const riskScore = total > 0 ? unverified / total : 0;
    
    scores.push({
      date: date.toISOString().split('T')[0],
      risk_score: Math.min(1, Math.max(0, riskScore)),
      verified: verified,
      unverified: unverified
    });
  }
  
  return scores;
};

// Dummy risk scores data
const DUMMY_RISK_SCORES = {
  total_risk_score: 0.65,
  daily_risk_scores: generateDailyRiskScores()
};

const useSecurityStore = create((set, get) => ({
  // Existing repositories/findings state
  repositories: [],
  pagination: {
    has_next: false,
    last_page: 1,
    page: 1,
    per_page: 10,
    total: 0,
  },
  isLoading: false,
  error: null,
  dataLoaded: false, // <-- ADD THIS
  
  // New risk scores state
  riskScores: {
    total_risk_score: 0,
    daily_risk_scores: []
  },
  riskScoresLoading: false,
  riskScoresError: null,

  // Polling state
  pollingInterval: null,
  isPolling: false,
  pollingIntervalMs: 30000, // 30 seconds default
  initialLoadComplete: false, // Track if initial load is done

  // Basic setters
  setRepositories: (repositories) => set({ repositories }),
  setLoading: (isLoading) => set({ isLoading }),
  setError: (error) => set({ error }),
  setRiskScores: (riskScores) => set({ riskScores }),
  setRiskScoresLoading: (riskScoresLoading) => set({ riskScoresLoading }),
  setRiskScoresError: (riskScoresError) => set({ riskScoresError }),
  setPagination: (pagination) => set({ pagination }),
  resetInitialLoad: () => set({ initialLoadComplete: false }),

  // Helper function to compare repositories for changes using lodash.isEqual
  hasRepositoryChanges: (oldRepos, newRepos) => {
    if (!oldRepos || !newRepos) return true;
    if (oldRepos.length !== newRepos.length) return true;
    
    // Use lodash.isEqual for deep comparison of the entire arrays
    // This will catch any changes in repository properties, including nested objects
    return !isEqual(oldRepos, newRepos);
  },

  // Fetch findings using dummy data
  fetchFindings: async (page = 1, per_page = 10, forceUpdate = false) => {
    try {
      const { repositories: currentRepos } = get();
      
      // Only show loading if it's the first load or forced update
      if (forceUpdate || !currentRepos || currentRepos.length === 0) {
        set({ isLoading: true, error: null });
      }
      
      // Simulate API delay
      await new Promise(resolve => setTimeout(resolve, 300));
      
      // Paginate dummy data
      const startIndex = (page - 1) * per_page;
      const endIndex = startIndex + per_page;
      const paginatedRepos = DUMMY_REPOSITORIES.slice(startIndex, endIndex);
      
      const newPagination = {
        has_next: endIndex < DUMMY_REPOSITORIES.length,
        last_page: Math.ceil(DUMMY_REPOSITORIES.length / per_page),
        page: page,
        per_page: per_page,
        total: DUMMY_REPOSITORIES.length,
      };
      
      // Check if there are actual changes in repositories or pagination
      const hasRepoChanges = get().hasRepositoryChanges(currentRepos, paginatedRepos);
      const { pagination: currentPagination } = get();
      const hasPaginationChanges = !isEqual(currentPagination, newPagination);
      const hasChanges = hasRepoChanges || hasPaginationChanges;
      
      if (hasChanges || forceUpdate) {
        set((state) => ({
          ...state,
          repositories: paginatedRepos,
          pagination: newPagination,
          isLoading: false,
          initialLoadComplete: true
        }));
      } else {
        // Only update loading state if it was set, without touching other state
        set((state) => {
          if (state.isLoading) {
            return { ...state, isLoading: false, initialLoadComplete: true };
          }
          return state;
        });
      }
      
    } catch (error) {
      console.error('Error fetching findings:', error);
      set({ 
        error: 'Failed to fetch findings',
        isLoading: false 
      });
    }
  },

  // Fetch risk scores using dummy data
  fetchRiskScores: async () => {
    try {
      set({ riskScoresLoading: true, riskScoresError: null });
      
      // Simulate API delay
      await new Promise(resolve => setTimeout(resolve, 300));
      
      // Use dummy risk scores data
      const riskScores = {
        total_risk_score: DUMMY_RISK_SCORES.total_risk_score,
        daily_risk_scores: DUMMY_RISK_SCORES.daily_risk_scores.map(day => ({
          date: day.date,
          risk_score: typeof day.risk_score === 'number' ? day.risk_score : 0,
          verified: typeof day.verified === 'number' ? day.verified : 0,
          unverified: typeof day.unverified === 'number' ? day.unverified : 0
        }))
      };
      
      set({ 
        riskScores: riskScores,
        riskScoresLoading: false 
      });
      
    } catch (error) {
      console.error('Error fetching risk scores:', error);
      set({ 
        riskScoresError: 'Failed to fetch risk scores',
        riskScoresLoading: false 
      });
    }
  },

  // Load initial data (both findings and risk scores)
  loadInitialData: async (page = 1, per_page = 10) => {
    const { fetchFindings, fetchRiskScores } = get();
    
    try {
      // Fetch both concurrently for better performance
      await Promise.all([
        fetchFindings(page, per_page),
        fetchRiskScores()
      ]);
      set({ dataLoaded: true });
      return true; // Return success status
    } catch (error) {
      console.error('Error loading initial data:', error);
      set({ dataLoaded: false });
      // Individual errors are already handled in fetchFindings and fetchRiskScores
      throw error; // Re-throw to be handled by caller
    }
  },

  // Start polling for findings (keeping risk scores separate)
  startPolling: (intervalMs = 3000) => {
    const { stopPolling, fetchFindings, repositories, pagination, initialLoadComplete } = get();
    
    // Stop any existing polling
    stopPolling();
    
    // Don't start polling if initial load isn't complete
    if (!initialLoadComplete) {
      //console.log('⏳ Skipping polling start - initial load not complete yet');
      return;
    }
    
    // Start new polling interval
    const intervalId = setInterval(() => {
      // Only poll if we have repositories and initial load is complete
      if (repositories && repositories.length > 0 && initialLoadComplete) {
        // Always use current pagination values from state
        const currentPage = pagination.page || 1;
        const currentPerPage = pagination.per_page || 10;
        fetchFindings(currentPage, currentPerPage);
      }
    }, intervalMs);
    
    set({ 
      pollingInterval: intervalId,
      isPolling: true,
      pollingIntervalMs: intervalMs
    });
    
    //console.log(`Started smart polling findings every ${intervalMs}ms`);
  },

  // Stop polling
  stopPolling: () => {
    const { pollingInterval } = get();
    
    if (pollingInterval) {
      clearInterval(pollingInterval);
      set({ 
        pollingInterval: null,
        isPolling: false
      });
      //console.log('Stopped polling findings');
    }
  },

  // not used yet
  // refreshRiskScores: async () => {
  //   const { fetchRiskScores } = get();
  //   await fetchRiskScores();
  // },

  // Get aggregated counts - FIXED to use both repositories and risk scores
  getAggregatedCounts: () => {
    const { riskScores } = get();
    
    // // Try to get data from repositories first (most recent data)
    // if (repositories && repositories.length > 0) {
    //   const totals = repositories.reduce(
    //     (acc, repo) => ({
    //       verifiedFindings: acc.verifiedFindings + (repo.verifiedFindings || 0),
    //       unverifiedFindings: acc.unverifiedFindings + (repo.unverifiedFindings || 0)
    //     }),
    //     { verifiedFindings: 0, unverifiedFindings: 0 }
    //   );
      
    //   // If we have some data from repositories, return it
    //   if (totals.verifiedFindings > 0 || totals.unverifiedFindings > 0) {
    //     return totals;
    //   }
    // }
    
    // Fallback to risk scores data (daily aggregation)
    if (riskScores.daily_risk_scores && riskScores.daily_risk_scores.length > 0) {
      // Get the most recent day's data
      const latestDay = riskScores.daily_risk_scores[riskScores.daily_risk_scores.length - 1];
      return {
        verifiedFindings: latestDay.verified || 0,
        unverifiedFindings: latestDay.unverified || 0
      };
    }
    
    // Default fallback
    return { verifiedFindings: 0, unverifiedFindings: 0 };
  },

  // Get daily risk scores formatted for charts - IMPROVED
  getDailyRiskScores: () => {
    const { riskScores } = get();
    
    if (!riskScores.daily_risk_scores || riskScores.daily_risk_scores.length === 0) {
      //console.log('No daily risk scores available');
      return [];
    }
    
    const processedScores = riskScores.daily_risk_scores.map(day => {
      const processed = {
        date: day.date,
        risk_score: typeof day.risk_score === 'number' ? day.risk_score : 0,
        verified: typeof day.verified === 'number' ? day.verified : 0,
        unverified: typeof day.unverified === 'number' ? day.unverified : 0
      };
      
      // //console.log(`Processing day ${day.date}:`, {
      //   original: day,
      //   processed: processed
      // //});
      
      return processed;
    });
    
    //console.log('All processed daily risk scores:', processedScores);
    return processedScores;
  },

  

  // Get total risk score
  getTotalRiskScore: () => {
    const { riskScores, riskScoresError, error } = get();
    
    // Return 0 if there are any errors or no data
    if (riskScoresError || error || !riskScores || riskScores.total_risk_score === null || riskScores.total_risk_score === undefined) {
      return 0;
    }
    
    const score = riskScores.total_risk_score;
    
    // Ensure the score is a valid number between 0 and 1
    if (typeof score !== 'number' || isNaN(score) || score < 0 || score > 1) {
      return 0;
    }
    
    return score;
  },

  getLatestRiskScore: () => {
    const { riskScores } = get();
    
    if (!riskScores.daily_risk_scores || riskScores.daily_risk_scores.length === 0) {
      return 0;
    }
    
    const lastDay = riskScores.daily_risk_scores[riskScores.daily_risk_scores.length - 1];
    return lastDay.risk_score || 0;
  },


  // Get repositories with filters
  getFilteredRepositories: (filters = {}) => {
    const { repositories } = get();
    let filtered = [...repositories];
    
    // Example filters - adjust based on your data structure
    if (filters.hasFindings) {
      filtered = filtered.filter(repo => 
        (repo.verifiedFindings || 0) > 0 || (repo.unverifiedFindings || 0) > 0
      );
    }
    
    if (filters.riskLevel) {
      filtered = filtered.filter(repo => {
        const totalFindings = (repo.verifiedFindings || 0) + (repo.unverifiedFindings || 0);
        const riskScore = totalFindings > 0 ? (repo.verifiedFindings || 0) / totalFindings : 0;
        
        switch (filters.riskLevel) {
          case 'high':
            return riskScore > 0.7;
          case 'medium':
            return riskScore > 0.3 && riskScore <= 0.7;
          case 'low':
            return riskScore <= 0.3;
          default:
            return true;
        }
      });
    }
    //console.log(repositories);
    return filtered;
  },

  // Cleanup function
  cleanup: () => {
    const { stopPolling } = get();
    
    // Stop any active polling
    stopPolling();
 
    set({
      repositories: [],
      isLoading: false,
      error: null,
      riskScores: {
        total_risk_score: 0,
        daily_risk_scores: []
      },
      riskScoresLoading: false,
      riskScoresError: null,
      pollingInterval: null,
      isPolling: false,
      dataLoaded: false, // <-- RESET THIS
      pagination: {
        has_next: false,
        last_page: 1,
        page: 1,
        per_page: 10,
        total: 0,
      }
    });
  },

  // Reset errors
  clearErrors: () => {
    set({ 
      error: null,
      riskScoresError: null 
    });
  },

  // Force refresh all data
  refreshAllData: async () => {
    const { loadInitialData } = get();
    await loadInitialData();
  }
}));

export default useSecurityStore;
export { useSecurityStore };




    



// extra functions not used.
  // // Get summary statistics
  // getSummaryStats: () => {
  //   const { repositories, riskScores } = get();
  //   const aggregated = get().getAggregatedCounts();
    
  //   return {
  //     totalRepositories: repositories.length,
  //     totalFindings: aggregated.verifiedFindings + aggregated.unverifiedFindings,
  //     verifiedFindings: aggregated.verifiedFindings,
  //     unverifiedFindings: aggregated.unverifiedFindings,
  //     totalRiskScore: riskScores.total_risk_score || 0,
  //     averageRiskScore: repositories.length > 0 
  //       ? (riskScores.total_risk_score || 0) / repositories.length 
  //       : 0,
  //     repositoriesWithFindings: repositories.filter(repo => 
  //       (repo.verifiedFindings || 0) > 0 || (repo.unverifiedFindings || 0) > 0
  //     ).length
  //   };
  // },

  // // Update specific repository
  // updateRepository: (id, updates) => {
  //   const { repositories } = get();
  //   const updatedRepos = repositories.map(repo => 
  //     repo.id === id ? { ...repo, ...updates } : repo
  //   );
  //   set({ repositories: updatedRepos });
  // },

  // // Add new repository
  // addRepository: (repository) => {
  //   const { repositories } = get();
  //   set({ repositories: [...repositories, repository] });
  // },

  // // Remove repository
  // removeRepository: (id) => {
  //   const { repositories } = get();
  //   set({ repositories: repositories.filter(repo => repo.id !== id) });
  // },
